import * as React from 'react';
import cx from 'classnames';
import { map } from 'lodash';

import { ArrowheadDownIcon } from '@frontend/app/components';
import { EditorState } from 'draft-js';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/components/ui/popover';
import { Button } from '@/shadcn/components/ui/button';

import { TBlockType } from './types';
import { hasListType } from './helpers';

const { useState } = React;

const BLOCK_TYPE_LABEL: {
  [key in TBlockType]: string;
} = {
  'unstyled': 'Paragraph',
  'ordered-list-item': 'Numbered List',
  'unordered-list-item': 'Bulleted List',
  'header-one': 'Heading 1',
  'header-two': 'Heading 2',
  'header-three': 'Heading 3',
  'header-four': 'Heading 4',
  'header-five': 'Heading 5',
  'header-six': 'Heading 6',
};
const SUPPORTED_BLOCK_TYPES: TBlockType[] = [
  'unstyled',
  'header-one', 'header-two', 'header-three',
  'header-four', 'header-five', 'header-six',
];
const BLOCK_TYPE_FONT_SIZE: {
  [key in TBlockType]?: string;
} = {
  'unstyled': '1em',
  'header-one': '2em',
  'header-two': '1.5em',
  'header-three': '1.17em',
  'header-four': '1em',
  'header-five': '.83em',
  'header-six': '.67em',
};
const BLOCK_TYPE_HOTKEY: {
  [key in TBlockType]?: string;
} = {
  'unstyled': '⌘0',
  'header-one': '⌘1',
  'header-two': '⌘2',
  'header-three': '⌘3',
  'header-four': '⌘4',
  'header-five': '⌘5',
  'header-six': '⌘6',
};

interface IProps {
  editorState: EditorState;
  toggleBlockType(blockType: TBlockType): void;

  className?: string;
}

/**
 * @type {React.FC}
 */
export const BlockTypes: React.FC<IProps> = React.memo((props) => {
  const { editorState } = props;
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const selection = editorState.getSelection();
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType() as TBlockType;

  return (
    <Popover open={dropdownVisible} onOpenChange={setDropdownVisible} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cx(
            'flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-grey-1 transition-colors duration-150 min-w-32 bg-white border border-grey-2 h-6 text-xs',
            {
              'opacity-50 cursor-not-allowed': hasListType(blockType),
              'bg-grey-1': dropdownVisible,
            },
            props.className
          )}
          disabled={hasListType(blockType)}
          onMouseDown={(event) => event.preventDefault()}
        >
          <span className="text-sm font-medium flex-1 text-grey-6 text-left">{BLOCK_TYPE_LABEL[blockType]}</span>
          <ArrowheadDownIcon size={14} className="text-grey-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-2 z-[9999] rounded-2xl">
        <div className="flex flex-col gap-2 overflow-auto">
          {map(SUPPORTED_BLOCK_TYPES, (type) => (
            <div
              key={type}
              className={cx(
                'cursor-pointer hover:bg-grey-1 flex items-center justify-between transition-colors duration-150 rounded-lg p-2',
                {
                  'bg-grey-1 text-grey-2': type === blockType,
                }
              )}
              style={{
                fontSize: BLOCK_TYPE_FONT_SIZE[type],
              }}
              onMouseDown={(event) => {
                event.preventDefault();
                props.toggleBlockType(type);
                setDropdownVisible(false);
              }}
            >
              <span className="font-medium text-grey-6">
                {type === 'unstyled' ? 'Paragraph' : BLOCK_TYPE_LABEL[type]}
              </span>
              <span className="text-xs text-grey-4 bg-grey-1 px-1.5 py-0.5 rounded">
                {BLOCK_TYPE_HOTKEY[type]}
              </span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
});

BlockTypes.displayName = 'BlockTypes';
